import {
  HiOutlineSearch,
} from "react-icons/hi";
import Swal from "sweetalert2";
import { getProfile, logout, getDashboardData } from "../ApiHelper/url_Helpers";
import { useNavigate } from "react-router-dom";
import { Pencil, LogIn, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { toasterError } from "./toaster";
import NotificationsOffcanvas from "./NotificationsOffcanvas";
import notificationsService from "../services/notificationsService";
import HourlyPunchModal from "./HourlyPunchModal";
import hourlyPunchService from "../services/hourlyPunchService";
import liveTrackingService from "../services/liveTrackingService";

export const Topbar = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profilepic, setProfilePic] = useState(
    sessionStorage.getItem("profilepic") || localStorage.getItem("profilepic")
  );
  const [username, setUserName] = useState(
    sessionStorage.getItem("username") || localStorage.getItem("username")
  );
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHourlyPunchModal, setShowHourlyPunchModal] = useState(false);
  const [hourlyPunchEnabled, setHourlyPunchEnabled] = useState(false);

  useEffect(() => {
    const updateCount = () => {
      // Use unread_count from API (stored in localStorage)
      const count = notificationsService.getUnreadCount();
      setUnreadCount(count);
    };

    updateCount();
    window.addEventListener("notificationsUpdated", updateCount);
    window.addEventListener("newNotification", updateCount);
    window.addEventListener("unreadCountUpdated", updateCount);
    return () => {
      window.removeEventListener("notificationsUpdated", updateCount);
      window.removeEventListener("newNotification", updateCount);
      window.removeEventListener("unreadCountUpdated", updateCount);
    };
  }, []);

  const userToken = sessionStorage.getItem("userToken") || localStorage.getItem("userToken");
  const isLoggedIn =
    (sessionStorage.getItem("isLoggedIn") || localStorage.getItem("isLoggedIn")) === "true";
  const hrmsUser =
    (sessionStorage.getItem("hrmsUser") || localStorage.getItem("hrmsUser")) === "true";
  const isAuthenticated = userToken && isLoggedIn && hrmsUser;

  // Listen to dashboard profile update or fetch permissions
  useEffect(() => {
    const handleDashboardProfile = (e) => {
      if (e?.detail) {
        setHourlyPunchEnabled(Boolean(e.detail.hourly_location_punch_enabled));
      }
    };

    window.addEventListener("dashboardProfileUpdated", handleDashboardProfile);

    // Initial check from dashboard data if user is logged in
    if (isAuthenticated) {
      getDashboardData()
        .then((res) => {
          if (res?.profile?.hourly_location_punch_enabled !== undefined) {
            setHourlyPunchEnabled(Boolean(res.profile.hourly_location_punch_enabled));
          }
        })
        .catch(() => {});
    }

    return () => {
      window.removeEventListener("dashboardProfileUpdated", handleDashboardProfile);
    };
  }, [isAuthenticated]);

  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to exit the session?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1a2333",
      cancelButtonColor: "#f1f5f9",
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      background: "#ffffff",
      color: "#1a1a1a",
      customClass: {
        popup: "rounded-[24px]",
        confirmButton: "px-6 py-2 rounded-lg font-semibold",
        cancelButton: "px-6 py-2 rounded-lg font-semibold text-gray-600",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const resp = await logout();
        if (resp?.user) {
          localStorage.setItem("isLoggedIn", "false");
          localStorage.setItem("hrmsUser", "false");
          localStorage.removeItem("userToken");
          sessionStorage.clear();
          localStorage.removeItem("authResponse");
          sessionStorage.removeItem("authResponse");
          localStorage.removeItem("profilepic");
          hourlyPunchService.stop();
          liveTrackingService.stop();
          Swal.fire({
            title: "Logged Out!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });

          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      }
    });
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await getProfile();
      const basic_info = response?.basic_info?.data || {};
      const personal_info = response?.personal_info?.data || {};
      setProfile(response);
      if (basic_info.profile_image) {
        setProfilePic(basic_info.profile_image);
        sessionStorage.setItem("profilepic", basic_info.profile_image);
        localStorage.setItem("profilepic", basic_info.profile_image);
      }
      if (basic_info.first_name) {
        const name = `${basic_info.first_name} ${basic_info.last_name || ""}`;
        setUserName(name);
        sessionStorage.setItem("username", name);
        localStorage.setItem("username", name);
      }
    } catch (error) {
      toasterError("Failed to fetch profile details");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchProfile();
    const handleUpdate = () => fetchProfile();
    window.addEventListener("profileUpdate", handleUpdate);
    return () => window.removeEventListener("profileUpdate", handleUpdate);
  }, []);

  return (
    <div
      className="h-16 bg-white flex items-center justify-between px-2 md:px-6 shadow-md fixed top-0 left-0 right-0 z-20
      md:ml-64 md:w-[calc(100%-16rem)]
    "
    >
      <div className="flex items-center mr-10">
        {/* Hamburger menu for mobile */}
        <button
          className="md:hidden text-gray-700 rounded-lg cursor-pointer"
          onClick={() => setOpen(!open)}
        ></button>
      </div>

      {/* Right Icons */}
      <div className="flex items-center gap-2 pr-4 md:gap-4">
        {/* Hourly Punch In Button - only rendered if hourly punch is enabled */}
        {/* {isAuthenticated && hourlyPunchEnabled && (
          <button
            onClick={() => setShowHourlyPunchModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg shadow-sm text-xs font-semibold cursor-pointer transition-all active:scale-95"
            title="Hourly Location Punch"
          >
            <Clock className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hourly Punch</span>
          </button>
        )} */}

        {/* User Name & Role */}
        <div className="hidden md:flex flex-col items-end mr-1">
          <span className="text-sm font-bold text-gray-800 leading-none">{username || "User"}</span>
        </div>

        <div
          onClick={() => navigate("/profile")}
          role="button"
          className="relative w-11 h-11 cursor-pointer"
        >
          {/* Avatar */}
          <div className="w-full h-full bg-primary text-white rounded-2xl flex items-center justify-center font-bold text-xl shadow-premium border-2 border-white transition-transform hover:rotate-3 active:scale-90 overflow-hidden">
            <img
              src={
                profilepic ||
                "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop"
              }
              alt="avatar"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Edit Icon */}
          <div
            className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md border"
            onClick={() => navigate("/profile")}
          >
            <Pencil size={12} className="text-gray-700" />
          </div>
        </div>

        <button
          onClick={() => setShowNotifications(true)}
          className="relative flex items-center justify-center w-5 h-5 md:w-8 md:h-8 border-2 border-gray-200 rounded-lg hover:border-[#0095d1] cursor-pointer"
        >
          <img
            src="/icons/notification.png"
            alt="bell"
            className="w-4 h-4 md:w-5 md:h-5 object-contain group-hover:brightness-110"
            draggable="false"
          />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
        <NotificationsOffcanvas open={showNotifications} onClose={() => setShowNotifications(false)} />

        {isAuthenticated ? (
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-5 h-5 md:w-8 md:h-8 border-2 border-gray-200 rounded-lg hover:border-[#0095d1] cursor-pointer"
            title="Logout"
          >
            <img
              src="/icons/logout.png"
              alt="logout"
              className="w-3 h-3 md:w-5 md:h-5 object-contain group-hover:brightness-110"
              draggable="false"
            />
          </button>
        ) : (
          <button
            onClick={() => navigate("/login")}
            className="flex items-center justify-center gap-1 px-2 md:px-3 h-5 md:h-8 border-2 border-gray-200 rounded-lg hover:border-[#0095d1] cursor-pointer group transition-all"
            title="Login"
          >
            <LogIn className="w-3 h-3 md:w-4 md:h-4 text-gray-700 group-hover:text-[#0095d1]" />
            <span className="hidden md:inline text-xs font-bold text-gray-700 group-hover:text-[#0095d1]">
              Login
            </span>
          </button>
        )}
      </div>

      <HourlyPunchModal
        isOpen={showHourlyPunchModal}
        onClose={() => setShowHourlyPunchModal(false)}
      />
    </div>
  );
};
